import React, { useState } from 'react';
import { XMarkIcon, PhotoIcon, FilmIcon } from '@heroicons/react/24/outline';
import { storyService } from '../services/storyService';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2 Mo
const MAX_VIDEO_SIZE = 5 * 1024 * 1024; // 5 Mo

const storySchema = z
  .object({
    media_type: z.enum(['image', 'video']),
    admin_target: z.enum(['all', 'shop', 'service']),
    caption: z.string().max(255, "255 caractères maximum").optional(),
    media: z.any().refine((file) => file instanceof File, "Le fichier est requis"),
  })
  .superRefine((data, ctx) => {
    if (!(data.media instanceof File)) return;
    const maxSize = data.media_type === 'video' ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (data.media.size > maxSize) {
      const maxMo = data.media_type === 'video' ? 5 : 2;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['media'],
        message: `Le fichier ne doit pas dépasser ${maxMo} Mo.`,
      });
    }
  });

const StoryModal = ({ isOpen, onClose, onSuccess }) => {
  const [preview, setPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(storySchema),
    defaultValues: { media_type: 'image', admin_target: 'all', caption: '' },
    mode: 'onChange',
  });

  const mediaType = watch('media_type');
  const adminTarget = watch('admin_target');

  const handleClose = () => {
    reset({ media_type: 'image', admin_target: 'all', caption: '' });
    setPreview(null);
    setErrorMessage('');
    onClose();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setErrorMessage('');

    const maxSize = mediaType === 'video' ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (file.size > maxSize) {
      const maxMo = mediaType === 'video' ? 5 : 2;
      setErrorMessage(`Le fichier ne doit pas dépasser ${maxMo} Mo.`);
      return;
    }

    setValue('media', file, { shouldValidate: true, shouldDirty: true });

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await storyService.createStory(data);
      onSuccess();
      handleClose();
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || "Une erreur est survenue lors de la publication."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/10  flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl   max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-800">Nouvelle story</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Fermer"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <p className="text-sm text-gray-500">
            Cette story s'affichera toujours en première position dans la barre de
            stories de l'application mobile et sera automatiquement retirée après 24h.
          </p>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {errorMessage}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de média <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className={`flex items-center justify-center p-4 border rounded-md cursor-pointer transition-all ${
                mediaType === 'image' ? 'bg-primary/10 border-primary shadow-sm' : 'border-gray-300 hover:bg-gray-50'
              }`}>
                <input type="radio" {...register('media_type')} value="image" className="sr-only" />
                <PhotoIcon className={`h-6 w-6 mr-2 ${mediaType === 'image' ? 'text-primary' : 'text-gray-400'}`} />
                <span className={mediaType === 'image' ? 'font-medium text-primary' : 'text-gray-700'}>Image</span>
              </label>

              <label className={`flex items-center justify-center p-4 border rounded-md cursor-pointer transition-all ${
                mediaType === 'video' ? 'bg-primary/10 border-primary shadow-sm' : 'border-gray-300 hover:bg-gray-50'
              }`}>
                <input type="radio" {...register('media_type')} value="video" className="sr-only" />
                <FilmIcon className={`h-6 w-6 mr-2 ${mediaType === 'video' ? 'text-primary' : 'text-gray-400'}`} />
                <span className={mediaType === 'video' ? 'font-medium text-primary' : 'text-gray-700'}>
                  Vidéo (max 20 Mo)
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Audience <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'all', label: 'Les deux' },
                { value: 'shop', label: 'Boutiques' },
                { value: 'service', label: 'Services' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center justify-center py-2.5 px-2 border rounded-md cursor-pointer transition-all text-sm ${
                    adminTarget === option.value
                      ? 'bg-primary/10 border-primary shadow-sm font-medium text-primary'
                      : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <input type="radio" {...register('admin_target')} value={option.value} className="sr-only" />
                  {option.label}
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              Détermine sur quelle liste (produits et/ou services) cette story apparaîtra dans l'app mobile.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mediaType === 'image' ? 'Image' : 'Vidéo'} <span className="text-red-500">*</span>
            </label>

            <div className={`mt-1 flex justify-center rounded-lg border border-dashed p-6 ${
              errors.media ? 'border-red-300 bg-red-50' : preview ? 'border-primary/30 bg-primary/5' : 'border-gray-300 hover:border-gray-400'
            } transition-colors`}>
              <div className="text-center">
                {preview ? (
                  mediaType === 'image' ? (
                    <div className="relative">
                      <img src={preview} alt="Aperçu" className="mx-auto max-h-56 rounded-md object-contain" />
                      <button
                        type="button"
                        onClick={() => { setPreview(null); setValue('media', null, { shouldValidate: true }); }}
                        className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:bg-red-50 transition-colors"
                        aria-label="Supprimer l'image"
                      >
                        <XMarkIcon className="h-5 w-5 text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <video src={preview} className="max-h-56 mx-auto rounded-md" controls />
                      <button
                        type="button"
                        onClick={() => { setPreview(null); setValue('media', null, { shouldValidate: true }); }}
                        className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:bg-red-50 transition-colors"
                        aria-label="Supprimer la vidéo"
                      >
                        <XMarkIcon className="h-5 w-5 text-red-500" />
                      </button>
                    </div>
                  )
                ) : (
                  <>
                    {mediaType === 'image' ? (
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                    ) : (
                      <FilmIcon className="mx-auto h-12 w-12 text-gray-400" />
                    )}
                    <div className="mt-4 flex flex-col text-sm leading-6 text-gray-600">
                      <label
                        htmlFor="media-upload"
                        className="relative cursor-pointer rounded-md bg-white font-medium text-primary hover:text-primary-dark inline-block mx-auto mb-2 px-4 py-2 border border-primary/30 hover:bg-primary/5 transition-colors"
                      >
                        <span>Téléverser un fichier</span>
                        <input
                          id="media-upload"
                          type="file"
                          accept={mediaType === 'image' ? 'image/jpeg,image/png' : 'video/mp4,video/quicktime,video/x-msvideo'}
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                    </div>
                    <p className="text-xs leading-5 text-gray-600 mt-2">
                      {mediaType === 'image' ? 'PNG, JPG (max 2 Mo)' : 'MP4, MOV (max 5 Mo)'}
                    </p>
                  </>
                )}
              </div>
            </div>
            {errors.media && <p className="mt-1 text-sm text-red-600">{errors.media.message}</p>}
          </div>

          <div>
            <label htmlFor="caption" className="block text-sm font-medium text-gray-700 mb-1">
              Légende (optionnel)
            </label>
            <textarea
              id="caption"
              {...register('caption')}
              rows={2}
              placeholder="Un court message affiché sur la story..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary transition-colors"
            />
            {errors.caption && <p className="mt-1 text-sm text-red-600">{errors.caption.message}</p>}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Publication...' : 'Publier la story'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoryModal;
